/**
 * Canonical scan configuration — the single TS source of truth for
 * brand-visibility scan limits and engine selection.
 *
 * Consumed by BOTH sides of the product:
 *   - the Temporal worker activities (relative import,
 *     `../shared/scan-config.js`), and
 *   - the webapp (via the `@ripenn/temporal/scan-config` subpath
 *     export; `webapp/lib/billing/feature-limits.ts` re-derives its
 *     BillingProduct-keyed records from these values).
 *
 * The SQL mirror lives in `get_monthly_scan_usage`
 * (supabase/migrations) and is pinned to these numbers by
 * `sql_tests/ripenn_supabase/orgs/test_monthly_scan_usage.py`.
 *
 * This module must stay dependency-free: it is bundled into webapp
 * client code and into workflow-adjacent worker code.
 */

export type ScanPlan = "free" | "solo" | "pro" | "team";

export type ScanEngine = "chatgpt" | "claude" | "perplexity" | "gemini";

/**
 * Canonical engine ordering for brand visibility scans. Engines beyond a
 * plan's max are dropped from the tail.
 */
export const CANONICAL_ENGINES: readonly ScanEngine[] = [
  "chatgpt",
  "perplexity",
  "claude",
  "gemini",
];

/**
 * Per-plan max engines per scan. null = unlimited (all canonical
 * engines). Mirrored into PLAN_RESOURCE_LIMITS.maxEnginesPerScan in
 * webapp/lib/billing/feature-limits.ts.
 */
export const MAX_ENGINES_BY_PLAN: Record<ScanPlan, number | null> = {
  // free onboarding scan; scheduled scans for FREE are blocked by the
  // monthly allowance (MONTHLY_SCAN_RUN_ALLOWANCE.free = 0), not here
  free: 1,
  solo: 2,
  pro: 4,
  team: null,
};

/**
 * Per-plan cap on active prompts per project. null = unlimited.
 * Mirrored into PLAN_RESOURCE_LIMITS.maxPromptsPerProject in
 * webapp/lib/billing/feature-limits.ts. Prompts above the cap still
 * scan, but cost credits (shared/credit-pricing.ts).
 */
export const MAX_PROMPTS_BY_PLAN: Record<ScanPlan, number | null> = {
  free: 10,
  solo: 100,
  pro: 500,
  team: null,
};

/**
 * The prompt cap for a plan string from the DB. Unknown plans get the
 * free cap, the most restrictive.
 */
export function promptCapForPlan(plan: string): number | null {
  return plan in MAX_PROMPTS_BY_PLAN
    ? MAX_PROMPTS_BY_PLAN[plan as ScanPlan]
    : MAX_PROMPTS_BY_PLAN.free;
}

/**
 * Engines selected for a scan on a given plan. Unknown plan strings
 * (defensive — plans come from the DB as text) select zero engines.
 */
export function selectScanEngines(plan: string): ScanEngine[] {
  // `in` check (not `?? 0`): null is a legit value meaning "unlimited";
  // only genuinely-unknown plan strings fall back to zero engines.
  const max =
    plan in MAX_ENGINES_BY_PLAN ? MAX_ENGINES_BY_PLAN[plan as ScanPlan] : 0;
  if (max === 0) return [];
  if (max === null) return [...CANONICAL_ENGINES];
  return CANONICAL_ENGINES.slice(0, max);
}

/** True when `s` is one of the canonical engine names. */
export function isScanEngine(s: unknown): s is ScanEngine {
  return (
    typeof s === "string" &&
    (CANONICAL_ENGINES as readonly string[]).includes(s)
  );
}

/**
 * Engines for a scan: the plan's engines, narrowed to the project's
 * selection. null selects the plan's full set. Result keeps canonical
 * order. A selection that leaves nothing (empty, or all outside the
 * plan) falls back to the plan's engines, so a scan never runs empty
 * on a plan that has engines.
 */
export function resolveScanEngines(
  plan: string,
  selected: readonly string[] | null,
): ScanEngine[] {
  const planEngines = selectScanEngines(plan);
  if (selected === null) return planEngines;
  const picked = planEngines.filter((e) => selected.includes(e));
  return picked.length > 0 ? picked : planEngines;
}

/**
 * How often a project's scheduled scan fires. `monthly` is retained
 * for projects that already have it but is no longer offered.
 */
export type ScanFrequency = "daily" | "three_weekly" | "weekly" | "monthly";

/** Allowed repeats (runs per prompt per engine) for a project. */
export const SCAN_REPEATS_OPTIONS = [1, 2, 3] as const;

export type ScanRepeats = (typeof SCAN_REPEATS_OPTIONS)[number];

export const DEFAULT_SCAN_REPEATS: ScanRepeats = 1;

export function isScanRepeats(n: unknown): n is ScanRepeats {
  return (SCAN_REPEATS_OPTIONS as readonly unknown[]).includes(n);
}

/**
 * Per-engine ceilings on runs per prompt per scan. An engine listed
 * here runs at most this many times per prompt regardless of the
 * scan's base run count (the project's repeats setting).
 *
 * gemini: every brand-visibility Gemini call carries Google Search
 * grounding, and grounded requests draw on one account-wide daily
 * allowance (1,500/day for Gemini 3 as of 2026-09-14). Three runs per
 * prompt exhausted it in a handful of scans; one run triples the
 * headroom. Design: docs/specs/2026-09-14-gemini-run-cap-design.md.
 */
export const MAX_RUNS_BY_ENGINE: Partial<Record<ScanEngine, number>> = {
  gemini: 1,
};

/** Runs per prompt for one engine: the base count, capped per
 * engine. */
export function runsForEngine(engine: ScanEngine, baseRuns: number): number {
  const cap = MAX_RUNS_BY_ENGINE[engine];
  return cap === undefined ? baseRuns : Math.min(baseRuns, cap);
}

/**
 * Total checks a scan will make: Σ over engines of prompts × runs.
 * The one formula for `workflow_metadata.total_checks` and the
 * workflow's progress denominator.
 */
export function countScanChecks(
  promptCount: number,
  engines: readonly ScanEngine[],
  baseRuns: number,
): number {
  return engines.reduce(
    (sum, engine) => sum + promptCount * runsForEngine(engine, baseRuns),
    0,
  );
}

/**
 * Average scans a project fires in a month, by frequency. An estimate
 * for the settings page (4.3 = weeks in a month), not a billing input.
 */
export const SCANS_PER_MONTH: Record<ScanFrequency, number> = {
  daily: 30,
  three_weekly: 13,
  weekly: 4.3,
  monthly: 1,
};

/** Runs a project is expected to use in a month. */
export function projectedMonthlyRuns(
  runsPerScan: number,
  freq: ScanFrequency,
): number {
  return Math.round(runsPerScan * SCANS_PER_MONTH[freq]);
}

/**
 * Per-plan monthly run allowance: runs (1 prompt x 1 engine x 1
 * repeat) included in the plan, manual + scheduled combined. Counted
 * via usage rows for the `analytics` feature within the current UTC
 * calendar month. Runs beyond it cost credits
 * (shared/credit-pricing.ts).
 *
 * The old allowance was 4 scans a month; each is 600 runs on solo
 * (100 prompts x 2 engines x 3 runs), so the paid values are 4x the
 * per-scan figure. Use null to express "unlimited" without changing
 * the type.
 */
export const MONTHLY_SCAN_RUN_ALLOWANCE: Record<ScanPlan, number | null> = {
  free: 0,
  solo: 2400,
  pro: 20000,
  team: 40000,
};

/**
 * Pure allowance check. Returns { used, limit, remaining }.
 *
 * `remaining` is max(0, limit - used); a null allowance is unlimited
 * (limit and remaining are Infinity). Unknown plan strings get limit 0.
 */
export function remainingScanRuns(
  plan: string,
  used: number,
): { used: number; limit: number; remaining: number } {
  // `in` check (not `?? 0`): null means "unlimited"; only unknown plan
  // strings fall back to a zero limit.
  const limit =
    plan in MONTHLY_SCAN_RUN_ALLOWANCE
      ? MONTHLY_SCAN_RUN_ALLOWANCE[plan as ScanPlan]
      : 0;
  if (limit === null) {
    return {
      used,
      limit: Number.POSITIVE_INFINITY,
      remaining: Number.POSITIVE_INFINITY,
    };
  }
  return { used, limit, remaining: Math.max(0, limit - used) };
}

/**
 * Why a scheduled scan fire produced no scan. Mirrors the CHECK on
 * scheduled_scan_skips.reason; the worker writes these and the webapp's
 * monitoring settings read them.
 *
 * - quota_exceeded: the plan has no monthly runs at all (free), or the
 *   allowance covered none of the scan's runs and the org's credits
 *   couldn't pay for them.
 * - insufficient_credits: the allowance covered part of the scan (a
 *   split scan) or all of its in-cap runs, and the credits for the
 *   rest (runs past the allowance, prompts above the plan's cap) fell
 *   short.
 */
export const SCHEDULED_SCAN_SKIP_REASONS = [
  "quota_exceeded",
  "insufficient_credits",
  "no_prompts",
  "disabled",
] as const;

export type ScheduledScanSkipReason =
  (typeof SCHEDULED_SCAN_SKIP_REASONS)[number];

// ============================================================================
// ENGINE PROFILES
// ============================================================================
//
// The model id behind each engine a scan queries, versioned in code.
// A profile is named by the month it was introduced and never edited
// once a scan has run on it, so workflow_metadata.engine_profile and
// ai_responses.model_id always resolve to the exact models that
// produced a number. They live in this file, not a sibling, because
// the webapp reads them through the `@ripenn/temporal/scan-config`
// subpath and Turbopack cannot follow a `.js` re-export between TS
// sources here; temporal code imports them from ./engine-profiles.js.

/** Model ids per engine, as each provider's API names them. */
export interface BrandVisibilityModels {
  readonly openai: string;
  readonly claude: string;
  readonly perplexitySonar: string;
  readonly gemini: string;
}

export const ENGINE_PROFILES = {
  /** The models every scan has used since web grounding was enabled. */
  "2026-06": {
    openai: "gpt-5.2",
    claude: "claude-haiku-4-5-20251001",
    perplexitySonar: "sonar-pro",
    gemini: "gemini-3.1-flash-lite-preview",
  },
} as const satisfies Record<string, BrandVisibilityModels>;

export type EngineProfileName = keyof typeof ENGINE_PROFILES;

/** The profile scans run on unless one is requested explicitly. */
export const CURRENT_ENGINE_PROFILE: EngineProfileName = "2026-06";

export const ENGINE_PROFILE_NAMES = Object.keys(
  ENGINE_PROFILES,
) as EngineProfileName[];

export function isEngineProfileName(
  value: unknown,
): value is EngineProfileName {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(ENGINE_PROFILES, value)
  );
}

export interface ResolvedEngineProfile {
  name: EngineProfileName;
  models: BrandVisibilityModels;
}

/**
 * The profile a scan runs on: the named one, or the current default
 * when none is given. An unknown name is a programming error, never a
 * silent fallback, because the recorded profile must be the one used.
 */
export function resolveEngineProfile(
  name?: string | null,
): ResolvedEngineProfile {
  const resolved = name ?? CURRENT_ENGINE_PROFILE;
  if (!isEngineProfileName(resolved)) {
    throw new Error(
      `Unknown engine profile "${resolved}"; known: ${ENGINE_PROFILE_NAMES.join(", ")}`,
    );
  }
  return { name: resolved, models: ENGINE_PROFILES[resolved] };
}
